import { Component, h, Prop } from '@stencil/core';
import { store } from '@stencil/redux';
import { Unsubscribe } from 'redux';
import { RootState } from '../../../redux/reducers';
import { UnitConfig } from '../../../redux/types';
import { MileageOption, PriceCalculation, SelectOption, SelectStyle } from '../../../types/types';
import { formatPrice, t } from '../../../redux/utils';
import { getMileageAmountPerPeriod } from '../../../utils/mileage';

@Component({
  tag: 'rentware-select-mileage',
  styleUrl: 'style.sass',
  shadow: false,
})
export class RentwareSelectMileage {
  @Prop() selectStyle: SelectStyle = SelectStyle.Variations;
  @Prop() mileageOptions: MileageOption[];
  @Prop() selectedMileageId?: string;
  @Prop() available?: boolean;
  @Prop() isWaiting?: boolean;
  @Prop() onMileageChange: (id: string) => void;

  @Prop({ mutable: true }) displayNet: boolean;
  @Prop({ mutable: true }) unitConfig: UnitConfig;
  unsubscribe!: Unsubscribe;

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      return {
        unitConfig: state.system.unitsConfig,
        displayNet: state.system.pricing?.priceDisplay === 'net',
      };
    });
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  getMileagePrice(price, period: string = PriceCalculation.Interval) {
    const selectedPrice = this.displayNet ? price?.net : price?.gross;

    if (selectedPrice === undefined || selectedPrice === null) {
      return null;
    } else if (selectedPrice === 0 || selectedPrice === '0') {
      return t('units.included');
    } else {
      const isRelative = period !== PriceCalculation.Interval;
      if (!isRelative) {
        return formatPrice(selectedPrice, '+');
      }

      const mileagePeriod = t('units.' + period);
      const sign = '/';

      return formatPrice(selectedPrice, '+') + sign + mileagePeriod;
    }
  }

  get mileageSelectedPrice() {
    const selectedItem = this.mileageOptions?.find(item => item.id === this.selectedMileageId);

    return this.getMileagePrice(selectedItem?.relativePrice.price, selectedItem?.relativePrice.calculationType);
  }

  get mileageSelectOptions(): SelectOption[] {
    return this.mileageOptions?.map(item => {
      const relativePrice = item.relativePrice;
      const abbreviation = item.config.unit
      const label = getMileageAmountPerPeriod(
        item.relativeAmount.amount,
        item.amount > 0 ? 0 : item.config.includedAmount,
        item.relativeAmount.calculationType,
        t,
        abbreviation,
      );
      const labelPrice = this.getMileagePrice(relativePrice.price, relativePrice.calculationType);
      const labelSecondRow = item.relativeAmount.calculationType.toString() !== item.config.period.toString()
        ? '(' + getMileageAmountPerPeriod(
        item.config.amount,
        item.config.includedAmount,
        item.config.period,
        t,
        abbreviation,
      ) + ')'
        : null;

      return {
        label: label,
        labelSecondRow: labelSecondRow,
        labelPrice: labelPrice,
        value: item.id,
      };
    });
  }

  get unitLabel() {
    return t('units.' + this.mileageOptions?.[0]?.config?.unit)
  }

  render() {
    return (
      <rentware-select
        selectStyle={this.selectStyle}
        expandedStyle={SelectStyle.Variations}
        label={this.unitLabel}
        value={this.selectedMileageId}
        price={this.mileageSelectedPrice}
        onValueChange={e => this.onMileageChange(e.detail.value)}
        options={this.mileageSelectOptions}
        disabled={this.isWaiting}
      />
    );
  }
}
